import { useEffect, useState } from "react";
import { AlertTriangle, CheckCircle2, Copy, XCircle } from "lucide-react";
import BorderCard from "../campaigns/components/UserCampaigns/components/BorderCard";
import CardTitle from "../campaigns/components/UserCampaigns/components/CardTitle";
import SecondaryButton from "@/shared/components/ui/Button/SecondaryButton";
import Loader from "@/shared/components/ui/Loaders/Loader";
import {
  useGetDomainAuthentication,
  useVerifyDomainAuthentication,
} from "@/hooks/useDeliverability";
import { useMainStore } from "@/stores";
import { USER_ROLES } from "@/shared/utils/constants";

const STATUS_LABELS = {
  verified: "Verified",
  missing: "Missing",
  invalid: "Needs attention",
  error: "Check failed",
  not_checked: "Not checked",
};

function StatusIcon({ status }) {
  if (status === "verified") return <CheckCircle2 size={17} className="text-emerald-600" />;
  if (status === "error") return <AlertTriangle size={17} className="text-amber-600" />;
  return <XCircle size={17} className="text-red-500" />;
}

function RecordRow({ label, record, copied, onCopy }) {
  return (
    <div className="rounded-lg border border-gray-200 dark:border-gray-700 p-3">
      <div className="flex items-center justify-between gap-3">
        <span className="text-xs font-semibold text-gray-800 dark:text-gray-100">{label}</span>
        {record.copyable !== false && (
          <button
            type="button"
            className="inline-flex items-center gap-1 text-[11px] font-medium text-secondary"
            onClick={() => onCopy(record.value, label)}
          >
            <Copy size={13} /> {copied === label ? "Copied" : "Copy value"}
          </button>
        )}
      </div>
      <div className="mt-2 grid grid-cols-[70px_1fr] gap-x-2 gap-y-1 text-[11px]">
        <span className="text-gray-500">Host</span>
        <code className="break-all text-gray-700 dark:text-gray-200">{record.host}</code>
        <span className="text-gray-500">Type</span>
        <code className="text-gray-700 dark:text-gray-200">{record.type}</code>
        <span className="text-gray-500">Value</span>
        <code className="break-all text-gray-700 dark:text-gray-200">{record.value}</code>
      </div>
    </div>
  );
}

export default function DomainAuthenticationCard() {
  const user = useMainStore((state) => state.user);
  const canManage = [
    USER_ROLES.ORG_ADMIN,
    USER_ROLES.SUPER_ADMIN,
    USER_ROLES.SUPER_ADMIN_ALT,
  ].includes(user?.role);
  const { data, isLoading, isError } = useGetDomainAuthentication();
  const { mutate: verify, isPending } = useVerifyDomainAuthentication();
  const [selectors, setSelectors] = useState("");
  const [copied, setCopied] = useState("");

  useEffect(() => {
    setSelectors((data?.dkimSelectors || []).join(", "));
  }, [data?.dkimSelectors]);

  const copy = async (value, label) => {
    await navigator.clipboard.writeText(value);
    setCopied(label);
    window.setTimeout(() => setCopied(""), 1500);
  };

  const checks = data?.checks || {};
  const records = data?.setup?.records;

  return (
    <div className="mt-6">
      <BorderCard>
        <CardTitle
          text="Company Domain Authentication"
          description="Configure DNS outside SalesWorX, then verify SPF, DKIM, and DMARC here."
        />
      </BorderCard>
      <BorderCard variant="bottom" padding="p-5">
        {isLoading ? (
          <Loader text="Loading domain authentication..." />
        ) : isError || !data ? (
          <p className="text-sm text-red-600">Unable to load the company email domain.</p>
        ) : (
          <div className="flex flex-col gap-4">
            <div className="flex flex-wrap items-start justify-between gap-3">
              <div>
                <p className="text-sm font-semibold text-gray-900 dark:text-gray-100">{data.domain}</p>
                <p className="text-xs text-gray-500">{data.setup?.providerLabel}</p>
                {data.setup?.helpUrl && (
                  <a
                    href={data.setup.helpUrl}
                    target="_blank"
                    rel="noreferrer"
                    className="text-[11px] font-medium text-secondary hover:underline"
                  >
                    Open provider setup guide
                  </a>
                )}
              </div>
              <span className={`rounded-full px-2.5 py-1 text-xs font-semibold ${
                data.status === "verified"
                  ? "bg-emerald-50 text-emerald-700"
                  : "bg-amber-50 text-amber-700"
              }`}>
                {data.status === "verified"
                  ? data.lastError ? "Previously verified" : "DNS verified"
                  : "Setup incomplete"}
              </span>
            </div>

            <div className="grid grid-cols-1 gap-2 sm:grid-cols-3">
              {["spf", "dkim", "dmarc"].map((key) => {
                const status = checks[key]?.status || "not_checked";
                return (
                  <div key={key} className="flex items-center gap-2 rounded-lg border border-gray-200 dark:border-gray-700 p-3">
                    <StatusIcon status={status} />
                    <div>
                      <p className="text-xs font-semibold uppercase text-gray-800 dark:text-gray-100">{key}</p>
                      <p className="text-[11px] text-gray-500">{STATUS_LABELS[status] || status}</p>
                    </div>
                  </div>
                );
              })}
            </div>

            {records && (
              <div className="grid grid-cols-1 gap-3 lg:grid-cols-3">
                <RecordRow label="SPF" record={records.spf} copied={copied} onCopy={copy} />
                <RecordRow label="DKIM" record={records.dkim} copied={copied} onCopy={copy} />
                <RecordRow label="DMARC" record={records.dmarc} copied={copied} onCopy={copy} />
              </div>
            )}

            <div className="flex flex-col gap-2 sm:flex-row sm:items-end sm:justify-between">
              <label className="flex flex-1 flex-col gap-1 text-xs font-medium text-gray-700 dark:text-gray-200">
                DKIM selector(s)
                <input
                  value={selectors}
                  onChange={(event) => setSelectors(event.target.value)}
                  disabled={!canManage}
                  placeholder="google or selector1, selector2"
                  className="h-9 rounded-lg border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-900 px-3 text-sm font-normal"
                />
              </label>
              {canManage ? (
                <SecondaryButton
                  title="Check Again"
                  onClick={() => verify(selectors.split(",").map((value) => value.trim()).filter(Boolean))}
                  disabled={isPending}
                  isLoading={isPending}
                />
              ) : (
                <p className="text-xs text-gray-500">Only an organization admin can recheck or change selectors.</p>
              )}
            </div>

            {data.lastError && (
              <p className="rounded-lg bg-amber-50 p-3 text-xs text-amber-800">Last check: {data.lastError}</p>
            )}
            <p className="text-[11px] text-gray-500">
              Last checked: {data.lastCheckedAt ? new Date(data.lastCheckedAt).toLocaleString() : "Not checked yet"}. This verifies DNS records; DNS changes are made in the company&apos;s domain and email-provider admin systems.
            </p>
          </div>
        )}
      </BorderCard>
    </div>
  );
}
